import { WP_API_URL } from "../constants/wordpress";
import { withApiTracking } from "../utils/trackApiRequest";

export class WordPressPageError extends Error {
  constructor(message, status) {
    super(message);
    this.name = "WordPressPageError";
    this.status = status;
  }
}

const DEFAULT_FETCH_RETRIES = 3;
const DEFAULT_RETRY_DELAY_MS = 400;
const MEDIA_FETCH_CONCURRENCY = 4;

function getWpApiBase() {
  if (process.env.NODE_ENV === "development") {
    return "/api/wp";
  }
  return WP_API_URL.replace(/\/+$/, "");
}

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function shouldRetryStatus(status) {
  return !status || status >= 500 || status === 429;
}

async function fetchWithRetry(url, options = {}, { retries = DEFAULT_FETCH_RETRIES } = {}) {
  let lastError;

  for (let attempt = 0; attempt <= retries; attempt += 1) {
    try {
      const response = await fetch(url, options);

      if (response.ok || !shouldRetryStatus(response.status) || attempt === retries) {
        return response;
      }

      lastError = new WordPressPageError(
        `WordPress request failed (${response.status})`,
        response.status,
      );
    } catch (err) {
      lastError = err;
      if (attempt === retries) throw err;
    }

    await delay(DEFAULT_RETRY_DELAY_MS * (attempt + 1));
  }

  throw lastError;
}

async function fetchPageOnce(slug, lang) {
  const params = new URLSearchParams({
    slug,
    _embed: "1",
  });

  if (lang) {
    params.set("lang", lang);
  }

  const url = `${getWpApiBase()}/wp/v2/pages?${params.toString()}`;
  const response = await fetchWithRetry(url, {
    headers: { Accept: "application/json" },
  });

  if (!response.ok) {
    throw new WordPressPageError(
      `WordPress page fetch failed (${response.status})`,
      response.status,
    );
  }

  const pages = await response.json();
  return pages[0] || null;
}

export async function fetchPageBySlug(slug, { lang } = {}) {
  if (!slug) {
    throw new WordPressPageError("Page slug is required", 400);
  }

  return withApiTracking(async () => {
    const preferredLangs = lang ? [lang] : [];
    if (lang && lang !== "ar") {
      preferredLangs.push("ar");
    }
    preferredLangs.push(null);

    for (const nextLang of preferredLangs) {
      const page = await fetchPageOnce(slug, nextLang);
      if (page) return page;
    }

    return null;
  });
}

export async function fetchMediaById(id) {
  if (!id) return null;

  const url = `${getWpApiBase()}/wp/v2/media/${id}`;

  try {
    const response = await fetchWithRetry(url, {
      headers: { Accept: "application/json" },
    });

    if (!response.ok) return null;

    const media = await response.json();
    const width = media.media_details?.width || media.width || null;
    const height = media.media_details?.height || media.height || null;

    return {
      url: media.source_url || null,
      width,
      height,
      alt: String(media.alt_text || "").trim() || stripHtml(media.title?.rendered || ""),
    };
  } catch {
    return null;
  }
}

async function mapWithConcurrency(items, mapper, concurrency = MEDIA_FETCH_CONCURRENCY) {
  if (items.length === 0) return [];

  const results = new Array(items.length);
  let nextIndex = 0;

  async function worker() {
    while (nextIndex < items.length) {
      const currentIndex = nextIndex;
      nextIndex += 1;
      results[currentIndex] = await mapper(items[currentIndex], currentIndex);
    }
  }

  const workers = Array.from(
    { length: Math.min(concurrency, items.length) },
    () => worker(),
  );
  await Promise.all(workers);

  return results;
}

export async function fetchMediaMap(ids = []) {
  return withApiTracking(async () => {
    const uniqueIds = [...new Set(ids.filter((id) => typeof id === "number" && id > 0))];
    const entries = await mapWithConcurrency(uniqueIds, async (id) => {
      const media = await fetchMediaById(id);
      return [id, media];
    });

    return Object.fromEntries(entries.filter(([, media]) => media?.url));
  }, { silent: true });
}

export function getFeaturedImageUrl(page) {
  return page?._embedded?.["wp:featuredmedia"]?.[0]?.source_url || null;
}

export function stripHtml(html = "") {
  return html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}
